// src/demo/tour.ts: the demo's guided tour (docs/DECISIONS.md, "Demo tour"; the owner's pick, direction A, "the
// chapter bar", 2026-09-24). The builder explores alone, in the order a family lives it:
//   1 As a Buyer      on a phone: Start Your Build, then his own new board stop by stop (Home, a room, Add Photos,
//                     Messages, People, the app). "Next: Your Team" only once the stops are behind him.
//   2 Your Team       signed in as the builder's team admin, on his own inquiry and its brief (on a laptop).
//   3 Your Business   a family weeks in (the seeded owner persona's board), then the Pipeline.
// The bar (DemoBar.tsx) reads where he is from the address and this browser's memory of the tour (localStorage,
// demo:tour). The demo resets every night, so memory from an earlier day is ignored. Nothing here is on outside the
// demo (site.demo). On the public test ride (site.trial) the memory belongs to the visitor's copy instead of the day,
// and where the app cannot be installed (PWA_MODE off, as there) the tour ends at People: the app stop would lead nowhere.
import { signal } from "@preact/signals";
import type { DemoPersona } from "../../shared/api";
import type { RouteMatch, RouteName } from "../app/router";
import { parsePwaMode, pwaAllows } from "../../shared/pwa";
import { api } from "../lib/api";
import { ApiError } from "../lib/http";
import { local } from "../lib/storage";
import { me, site } from "../state/session";

export type TourChapter = 0 | 1 | 2 | 3;
export type StopId = "form" | "home" | "room" | "photos" | "messages" | "people" | "app";
export type Device = "mobile" | "laptop";

export const CHAPTERS: readonly { n: 1 | 2 | 3; long: string; short: string; line: string; device: Device }[] = [
  { n: 1, long: "As a Buyer", short: "Buyer", line: "Send the form. Plan your board.", device: "mobile" },
  { n: 2, long: "Your Team", short: "Team", line: "Your inquiry, its brief, a reply.", device: "laptop" },
  { n: 3, long: "Your Business", short: "Business", line: "A family weeks in. The Pipeline.", device: "laptop" },
];

/** Part 1's stops, in order. `short` is the name at 360 to 374px, where the long one would crowd the line. */
export const STOPS: readonly { id: StopId; name: string; short?: string; hint: string }[] = [
  { id: "form", name: "The Form", hint: "Start Your Build" },
  { id: "home", name: "Home", hint: "Next Step and tracker" },
  { id: "room", name: "A Room", hint: "Love, keep or pass" },
  { id: "photos", name: "Add Photos", short: "Photos", hint: "AI suggests the room" },
  { id: "messages", name: "Messages", hint: "Write to the team" },
  { id: "people", name: "People", hint: "Invite your partner" },
  { id: "app", name: "The App", hint: "On your home screen" },
];
export const LAST_STOP = STOPS.length - 1;

export function stopIndex(id: StopId): number {
  return STOPS.findIndex((s) => s.id === id);
}

/** The app stop shows only where the app can be installed (before the site config loads, it counts). */
export function appStopShown(): boolean {
  const s = site.value;
  return !s || pwaAllows(parsePwaMode(s.pwa), "install");
}

/** The stops this tour walks: every one, or every one but the app. */
export function tourStops(): typeof STOPS {
  return appStopShown() ? STOPS : STOPS.slice(0, LAST_STOP);
}

/** The index of the tour's last stop. */
export function lastStop(): number {
  return tourStops().length - 1;
}

// ------------------------------------------------------------------ this browser's memory

export type TourMemory = {
  /** The local day it was written (the demo resets nightly: an earlier day's tour is gone). */
  day: string;
  /** His board, once he has one (the buyer's project). */
  pid: string | null;
  /** The furthest stop of part 1 he has reached (an index into STOPS), -1 before the form. */
  furthest: number;
  /** The part he last chose (the team side tells part 2 from part 3 by it). */
  chapter: 1 | 2 | 3;
};

export const MEMORY_KEY = "demo:tour";

export function localDay(now: number): string {
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** What the memory is kept for: the local day, or on the test ride the visitor's copy. */
export function memoryDay(now: number): string {
  const key = site.value?.trial?.key;
  return key ? `copy:${key}` : localDay(now);
}

export function freshMemory(now: number): TourMemory {
  return { day: memoryDay(now), pid: null, furthest: -1, chapter: 1 };
}

/** The stored memory if it is today's and well formed, else a fresh one. */
export function parseMemory(raw: unknown, now: number): TourMemory {
  const fresh = freshMemory(now);
  if (!raw || typeof raw !== "object") return fresh;
  const m = raw as Partial<TourMemory>;
  if (m.day !== fresh.day) return fresh;
  const furthest = typeof m.furthest === "number" && Number.isInteger(m.furthest) ? Math.max(-1, Math.min(LAST_STOP, m.furthest)) : -1;
  const chapter = m.chapter === 2 || m.chapter === 3 ? m.chapter : 1;
  return { day: fresh.day, pid: typeof m.pid === "string" && m.pid ? m.pid : null, furthest, chapter };
}

function stored(now = Date.now()): TourMemory {
  return parseMemory(local.getJSON<unknown>(MEMORY_KEY, null), now);
}

export const tour = signal<TourMemory>(stored());

// Another tab moved the tour on: this one follows.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === null || e.key === `hb:${MEMORY_KEY}`) tour.value = stored();
  });
}

function remember(next: TourMemory): void {
  tour.value = next;
  local.setJSON(MEMORY_KEY, next);
}

/**
 * Today's memory, read fresh from storage (another tab may have moved on) and kept at the furthest either knows, so a
 * stale tab never undoes progress. Where storage is blocked, this tab's own copy carries on.
 */
function current(now = Date.now()): TourMemory {
  const day = memoryDay(now);
  const mine = tour.peek().day === day ? tour.peek() : freshMemory(now);
  const disk = stored(now);
  if (disk.day !== day || (disk.furthest < 0 && !disk.pid)) return mine;
  return disk.furthest >= mine.furthest ? disk : { ...disk, furthest: mine.furthest, pid: mine.pid ?? disk.pid };
}

/**
 * He reached a stop of part 1 (on his board `pid`). Reaching is never undone: going back to Home keeps the rest. The
 * tour starts at the form: a board opened any other way (a persona under Other People, say) is shown stop by stop but
 * noted nowhere, so nothing is held back for it.
 */
export function noteStop(stop: StopId, pid: string | null): void {
  const m = current();
  if (m.furthest < 0 && stop !== "form") return;
  // Only his board moves the tour on: another family's (a persona under Other People) is looked at, not toured.
  if (pid && m.pid && pid !== m.pid) return;
  const i = stopIndex(stop);
  const next: TourMemory = { ...m, chapter: 1, furthest: Math.max(m.furthest, i), pid: pid ?? m.pid };
  if (next.furthest !== m.furthest || next.pid !== m.pid || next.chapter !== m.chapter || m !== tour.peek()) remember(next);
}

function noteChapter(chapter: 1 | 2 | 3): void {
  const m = current();
  if (m.chapter !== chapter || m !== tour.peek()) remember({ ...m, chapter });
}

// ------------------------------------------------------------------ where he is (pure)

const HOUSEHOLD_ROUTES: readonly RouteName[] = ["home", "rooms", "room", "item", "unsorted", "sort", "messages", "people", "settings", "choices"];
const TEAM_ROUTES: readonly RouteName[] = ["team", "teamProject", "teamProjectTab", "teamIdeas", "teamHomes", "teamStartYourBuild", "teamSettings", "teamPipeline", "teamMessages"];

/**
 * Which part an address belongs to: 0 for the demo's own pages and anything outside the parts. A family's board seen
 * by the team ("View Their Board") stays in the team's part.
 */
export function chapterOf(r: Pick<RouteMatch, "name">, m: Pick<TourMemory, "chapter">, staff = false): TourChapter {
  if (r.name === "teamPipeline") return 3;
  if (TEAM_ROUTES.includes(r.name) || (staff && HOUSEHOLD_ROUTES.includes(r.name))) return m.chapter === 3 ? 3 : 2;
  if (r.name === "start" || r.name === "startSent" || r.name === "install" || HOUSEHOLD_ROUTES.includes(r.name)) return 1;
  return 0;
}

/** The stop an address is, or null (Settings, say, is no stop: the bar keeps the furthest one). */
export function stopOf(r: Pick<RouteMatch, "name" | "query">): StopId | null {
  switch (r.name) {
    case "start":
    case "startSent":
      return "form";
    case "home":
      return r.query.get("app") === "1" ? "app" : r.query.get("add") === "photos" ? "photos" : "home";
    case "choices":
      return "home";
    case "rooms":
    case "room":
    case "item":
      return "room";
    case "sort":
    case "unsorted":
      return "photos";
    case "messages":
      return "messages";
    case "people":
      return "people";
    case "install":
      return "app";
    default:
      return null;
  }
}

/** Where a stop is on his board. The room stop opens `roomId` (the board's first room) when known. */
export function stopHref(stop: StopId, pid: string, roomId: string | null): string {
  switch (stop) {
    case "form":
      return "/start";
    case "home":
      return `/p/${pid}`;
    case "room":
      return roomId ? `/p/${pid}/rooms/${roomId}` : `/p/${pid}/rooms`;
    case "photos":
      return `/p/${pid}?add=photos`;
    case "messages":
      return `/p/${pid}/messages`;
    case "people":
      return `/p/${pid}/people`;
    case "app":
      return `/p/${pid}?app=1`;
  }
}

/** The bar's one action: a stop on his board, a part (which signs in as needed), a plain link, or a hint. */
export type NextStep =
  | { kind: "stop"; label: string; href: string }
  | { kind: "chapter"; label: string; short: string; chapter: 2 | 3 }
  | { kind: "link"; label: string; short: string; href: string }
  | { kind: "quiet"; label: string; hint: string };

export type BarModel =
  | { chapter: 0 }
  | { chapter: 1; stop: StopId; reached: number; next: NextStep }
  | { chapter: 2 | 3; next: NextStep };

/**
 * What the bar shows. Part 1 names the stop he is on (the furthest one on a page that is no stop) and leads to the
 * one after the furthest he reached; on the form, the way on is Send, so the bar only says so. "Next: Your Team"
 * comes only at the last stop.
 */
export function barModel(r: Pick<RouteMatch, "name" | "query" | "params">, m: TourMemory, roomId: string | null, staff = false): BarModel {
  const chapter = chapterOf(r, m, staff);
  if (chapter === 0) return { chapter: 0 };
  if (chapter === 1) {
    const here = stopOf(r);
    // A board page that is no stop (Settings) keeps the furthest stop, and is at least Home: he is on a board.
    const stop = here ?? STOPS[Math.max(1, Math.min(m.furthest, lastStop()))]!.id;
    const reached = Math.max(m.furthest, stopIndex(stop));
    const pid = r.params.pid ?? m.pid;
    let next: NextStep;
    if (stop === "form" || !pid) next = { kind: "quiet", label: "Your Board", hint: "Send the form to open your board" };
    else if (reached >= lastStop()) next = { kind: "chapter", label: "Your Team", short: "Team", chapter: 2 };
    else {
      const to = STOPS[reached + 1]!;
      next = { kind: "stop", label: to.name, href: stopHref(to.id, pid, roomId) };
    }
    return { chapter: 1, stop, reached, next };
  }
  if (chapter === 2) return { chapter: 2, next: { kind: "chapter", label: "Your Business", short: "Business", chapter: 3 } };
  return {
    chapter: 3,
    next:
      r.name === "teamPipeline"
        ? { kind: "link", label: "Other People", short: "People", href: "/demo#people" }
        : { kind: "link", label: "The Pipeline", short: "Pipeline", href: "/team/pipeline" },
  };
}

/** The room the room stop opens: the board's fullest (set by the family's shell while a board is on screen). */
export const tourRoom = signal<string | null>(null);

/**
 * While part 1 runs on his board (sent, the app stop not reached yet), the install card waits for its own stop. Only
 * on that board (`pid`, the board on screen), and only today.
 */
export function holdsInstall(m: TourMemory, now: number, pid: string | null = m.pid): boolean {
  return m.day === memoryDay(now) && m.chapter === 1 && !!m.pid && pid === m.pid && m.furthest >= 0 && m.furthest < LAST_STOP;
}

/** "The Whitakers" from the seeded owner's name ("Dana Whitaker"): the family's name on the landing. */
export function familyName(personas: readonly DemoPersona[] | null): string | null {
  const owner = personas?.find((p) => p.role === "owner" && p.projectId);
  const last = owner?.name.trim().split(/\s+/).pop();
  if (!last) return null;
  return `The ${last}${/s$/i.test(last) ? "es" : "s"}`;
}

// ------------------------------------------------------------------ moving between parts

/** Part 1 from the start: a fresh tour, signed out of any demo person, on Start Your Build. */
export async function startAsBuyer(): Promise<void> {
  remember({ ...freshMemory(Date.now()), furthest: 0 });
  if (me.value?.user) {
    await api.signOut().catch(() => undefined);
    window.location.assign("/start");
    return;
  }
  window.location.assign("/start");
}

/** What to say when a part didn't open: a limit's own words (the test ride's, worker/src/try/quotas.ts), else try again. */
export function chapterError(e: unknown): string {
  return e instanceof ApiError && (e.code === "limit_reached" || e.code === "rate_limited") ? e.message : "We couldn't open that. Try again.";
}

/**
 * Parts 2 and 3: signed in as the builder's team admin (the demo persona), then on his own inquiry (part 2: the
 * board this browser toured, else the newest Start Your Build inquiry, so a laptop finds the one sent from a phone)
 * or on the seeded family weeks in (part 3). A full page load, so nothing of the buyer's session carries over.
 */
export async function goToChapter(chapter: 2 | 3): Promise<void> {
  const { personas } = await api.demoPersonas();
  const admin = personas.find((p) => p.role === "team_admin");
  if (admin && me.value?.user?.id !== admin.userId) await api.demoSignIn({ personaId: admin.id });
  // Only once he is the team: a switch that failed leaves the tour where it was.
  noteChapter(chapter);
  if (chapter === 3) {
    const family = personas.find((p) => p.role === "owner" && p.projectId);
    window.location.assign(family?.projectId ? `/team/p/${family.projectId}#brief` : "/team");
    return;
  }
  const pid = tour.peek().pid ?? (await newestInquiry());
  window.location.assign(pid ? `/team/p/${pid}` : "/team?view=new");
}

async function newestInquiry(): Promise<string | null> {
  try {
    const { rows } = await api.teamProjects({});
    const sent = rows.filter((r) => r.source === "start-form" || r.source === "self-start");
    sent.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return sent[0]?.id ?? null;
  } catch {
    return null;
  }
}
